import {
  api,
  BUNDLE_UNDO_REDO_HISTORY_TAG,
  BundleCategoryItem,
  isEndpointLockError,
  useExtractToMutation,
  useLazyGetBundleCategoryListQuery,
} from '@/api';
import {
  BROADCAST_FORCE_CASE_OVERVIEW_CARDS_REFRESH,
  BROADCAST_FORCE_CATEGORY_LIST_REFRESH,
  BROADCAST_FORCE_TIMELINE_REFRESH,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
  useFletchersBroadcast,
} from '@/broadcast';
import { FILTER_EXTRACTED_KEY } from '@/constants/filters';
import {
  BundleType,
  makeSelectBundlePageDataByPageId,
  makeSelectBundlePageIdByPageIndex,
  selectBundleCategoriesList,
  selectBundleMetadataByStoreKey,
  selectBundlePageIds,
  selectBundlePagesData,
  selectBundleSections,
  selectBundleSelectedThumbnailIndexes,
  selectEnabledStates,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  selectViewportPageIndexInStore,
  setBundlePageIds,
  setBundleSections,
  setBundleSelectedThumbnailIndexes,
  useAppDispatch,
  useAppSelector,
  mergePageInfo,
  PageInfoData,
  popBusyTask,
  pushBusyTask,
  selectPageInfoCache,
} from '@/store';
import { removePageIdsFromTree, useGlobalToast } from '@repo/atomic-components';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useBundleInteractionExtractTo = () => {
  const globalToastRef = useGlobalToast();
  const dispatch = useAppDispatch();

  const { sendMessageWithoutResponse, sendMessageWithResponse } = useFletchersBroadcast();

  const categoriesListRef = useRef(null);

  const currentBundleMetadata = useAppSelector(selectBundleMetadataByStoreKey());
  const otherBundleId = currentBundleMetadata?.otherBundleId;
  const bundleId = useAppSelector(selectSharedDataBundleId);

  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const bundlePagesData = useAppSelector(selectBundlePagesData);
  const pageInfoCache = useAppSelector(selectPageInfoCache);

  const enabledStates = useAppSelector(selectEnabledStates);
  const bundleSections = useAppSelector(selectBundleSections);
  const bundlePageIds = useAppSelector(selectBundlePageIds);
  const categoriesList = useAppSelector(selectBundleCategoriesList);
  const bundleSelectedThumbnailIndexes = useAppSelector(selectBundleSelectedThumbnailIndexes);

  const getBundlePageIdByPageIndex = useAppSelector(makeSelectBundlePageIdByPageIndex);
  const getBundlePageDataByPageId = useAppSelector(makeSelectBundlePageDataByPageId);

  const [showCategoriesPopup, setShowCategoriesPopup] = useState<boolean>(false);
  const [showNewCategoryAutocomplete, setShowNewCategoryAutocomplete] = useState<boolean>(false);
  const [extractedCategoryName, setExtractedCategoryName] = useState<string>('');
  const [extractedDate, setExtractedDate] = useState<string | null>(null);
  const [triggerGetBundleCategoryList] = useLazyGetBundleCategoryListQuery();
  const viewportPageIndexInStore = useAppSelector(selectViewportPageIndexInStore);
  const pageId: string | undefined = getBundlePageIdByPageIndex(viewportPageIndexInStore);

  const [
    triggerExtractTo,
    {
      isLoading: isExtractToLoading,
      isSuccess: isExtractToSuccess,
      isError: isExtractToError,
      error: extractToError,
      reset: extractToReset,
      originalArgs: originalExtractToArgs,
    },
  ] = useExtractToMutation();

  useEffect(() => {
    if (bundleId) {
      triggerGetBundleCategoryList({ bundleId: bundleId });
    }
  }, [bundleId, triggerGetBundleCategoryList]);

  // Surface extract-to progress in the Autosaved slot.
  useEffect(() => {
    if (!isExtractToLoading) return;
    const count = originalExtractToArgs?.pageIds?.length ?? 0;
    const id = 'extract-to';
    dispatch(
      pushBusyTask({
        id,
        description: `Extracting ${count} ${count === 1 ? 'page' : 'pages'}...`,
      }),
    );
    return () => {
      dispatch(popBusyTask(id));
    };
  }, [isExtractToLoading, originalExtractToArgs?.pageIds?.length, dispatch]);

  // Listen for rename and move to new section events
  useEffect(() => {
    const handleCategoryListRefresh = () => {
      if (bundleId) {
        triggerGetBundleCategoryList({ bundleId: bundleId });
      }
    };

    window.addEventListener(BROADCAST_FORCE_CATEGORY_LIST_REFRESH, handleCategoryListRefresh);

    return () => {
      window.removeEventListener(BROADCAST_FORCE_CATEGORY_LIST_REFRESH, handleCategoryListRefresh);
    };
  }, [bundleId, triggerGetBundleCategoryList]);

  const categoriesVirtualList = useMemo(() => {
    return categoriesList.filter((category) => category.inBundles);
  }, [categoriesList]);

  const bundleSelectedThumbnailIds: string[] = useMemo(() => {
    return bundleSelectedThumbnailIndexes.map((pageIndex) => getBundlePageIdByPageIndex(pageIndex));
  }, [bundleSelectedThumbnailIndexes, getBundlePageIdByPageIndex]);

  const isMultipleThumbnailsSelected: boolean = useMemo(
    () => bundleSelectedThumbnailIds.length > 1,
    [bundleSelectedThumbnailIds.length],
  );

  const pageIdsNotInLightBundle: string[] = useMemo(() => {
    const pageIdsNotInLightBundle = bundleSelectedThumbnailIds
      .map((id) => {
        const item = getBundlePageDataByPageId(id);
        const cached = pageInfoCache[id];
        // Use cached value if available, or fallback to item data
        const isInLightBundle = cached?.inLightBundle ?? item?.inLightBundle;
        return { id, isInLightBundle };
      })
      .filter((inf) => !inf.isInLightBundle)
      .map((inf) => inf.id);
    return activeBundleType === BundleType.FULL ? pageIdsNotInLightBundle : [];
  }, [activeBundleType, bundleSelectedThumbnailIds, getBundlePageDataByPageId, pageInfoCache]);

  const getPageToScrollTo = useCallback(
    (extractedPageIds: string[]) => {
      // Simple rule: if array length > 2 and pages are consecutive (diff = 1), use index 1
      if (extractedPageIds.length > 2) {
        // Get page numbers from the first two elements to check if consecutive
        const firstPageData = getBundlePageDataByPageId(extractedPageIds[0]);
        const secondPageData = getBundlePageDataByPageId(extractedPageIds[extractedPageIds.length - 1]);
        if (firstPageData && secondPageData) {
          const pageDiff = Math.abs(firstPageData.originalPageNumber - secondPageData.originalPageNumber);
          if (pageDiff === 1) {
            // Use index 1 (second element) for consecutive range selections
            return extractedPageIds[1];
          }
        }
      }

      // Fallback: use last page (original behavior)
      return extractedPageIds[extractedPageIds.length - 1];
    },
    [getBundlePageDataByPageId],
  );

  useEffect(() => {
    if (isExtractToError && originalExtractToArgs) {
      extractToReset();
      // The endpoint lock has already shown a toast for this
      if (isEndpointLockError(extractToError)) return;
      globalToastRef.current?.show({
        severity: 'error',
        detail: `An error occurred while adding ${originalExtractToArgs.pageIds.length > 1 ? 'pages' : 'page'} to the Light Bundle`,
        life: 5000,
      });
      return;
    }

    if (isExtractToSuccess && originalExtractToArgs) {
      globalToastRef.current?.show({
        severity: 'success',
        detail: `Successfully extracted ${originalExtractToArgs.pageIds.length} ${originalExtractToArgs.pageIds.length > 1 ? 'pages' : 'page'} to ${extractedCategoryName}.`,
        life: 5000,
      });

      /* OLD BUNDLE ENDPOINT - DISABLED
      const newBundlePagesData = new Map<string, BundleItemDataExtended>(bundlePagesData.entries());
      const cacheUpdates: Record<string, PageInfoData> = {};

      originalExtractToArgs.pageIds.forEach((id: string) => {
        if (newBundlePagesData.has(id)) {
          newBundlePagesData.set(id, { ...newBundlePagesData.get(id)!, inLightBundle: true });
        }
        cacheUpdates[id] = { inLightBundle: true };
      });

      dispatch(mergePageInfo({ key: BundleType.FULL, data: cacheUpdates }));

      sendMessageWithoutResponse(BROADCAST_EXTRACT_PAGES_FROM_FULL_BUNDLE, { newBundlePagesData });

      dispatch(setBundlePagesData({ key: BundleType.FULL, data: newBundlePagesData }));
      */

      // NEW LOGIC ONLY
      const cacheUpdates: Record<string, PageInfoData> = {};
      originalExtractToArgs.pageIds.forEach((id) => {
        cacheUpdates[id] = { inLightBundle: true };
      });
      dispatch(mergePageInfo({ key: BundleType.FULL, data: cacheUpdates }));
      dispatch(setBundleSelectedThumbnailIndexes({ key: BundleType.FULL, data: [] }));
      dispatch(api.util.invalidateTags([{ type: BUNDLE_UNDO_REDO_HISTORY_TAG, id: originalExtractToArgs.bundleId }]));
      sendMessageWithoutResponse(BROADCAST_FORCE_TIMELINE_REFRESH, {
        reason: 'Extract success',
      });
      sendMessageWithoutResponse(BROADCAST_FORCE_CASE_OVERVIEW_CARDS_REFRESH, {
        reason: 'Extract success',
      });
      // Optimistically hide extracted pages from the sections tree when the filter is active
      if (enabledStates[FILTER_EXTRACTED_KEY]) {
        const extractedIds = originalExtractToArgs.pageIds;
        if (extractedIds.length) {
          const updatedSections = removePageIdsFromTree({ tree: bundleSections, pageIds: extractedIds });
          const idsSet = new Set(extractedIds);
          const updatedPageIds = bundlePageIds.filter((id) => !idsSet.has(id));
          dispatch(setBundleSections({ key: BundleType.FULL, data: updatedSections }));
          dispatch(setBundlePageIds({ key: BundleType.FULL, data: updatedPageIds }));
        }
      }

      setExtractedCategoryName('');
      setExtractedDate(null);
      extractToReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundlePagesData, isExtractToError, isExtractToSuccess, isMultipleThumbnailsSelected, originalExtractToArgs]);

  const virtualizer = useVirtualizer({
    count: categoriesVirtualList.length,
    getScrollElement: () => categoriesListRef.current,
    estimateSize: () => 36,
    horizontal: false,
    overscan: 10,
  });

  const handleClickNewCategory = useCallback(() => {
    setShowNewCategoryAutocomplete(true);
  }, [setShowNewCategoryAutocomplete]);

  const handleClickCancelNewCategory = useCallback(() => {
    setShowNewCategoryAutocomplete(false);
  }, [setShowNewCategoryAutocomplete]);

  const handleClickSubmitNewCategory = useCallback(
    (valueLocal: string | BundleCategoryItem) => {
      if (activeBundleType !== BundleType.FULL) {
        return;
      }

      //Multiple Pages to Extract
      const hasSelectedPages = pageIdsNotInLightBundle.length > 0;
      //Single Page to Extract
      const cachedSingle = pageId ? pageInfoCache[pageId] : undefined;
      const singleInLightBundle =
        cachedSingle?.inLightBundle ?? (pageId ? getBundlePageDataByPageId(pageId)?.inLightBundle : false);
      const hasSinglePageToExtract = pageId && !singleInLightBundle;

      // Not a single page to extract
      if (!hasSelectedPages && !hasSinglePageToExtract) {
        globalToastRef.current?.show({
          severity: 'info',
          detail: 'All selected pages are already in Light Bundle',
          life: 5000,
        });
        return;
      }

      if (bundleId) {
        //Which pages to extract: selected pages or current page
        const pageIdsToExtract = hasSelectedPages ? pageIdsNotInLightBundle : pageId ? [pageId] : [];

        if (pageIdsToExtract.length === 0) {
          globalToastRef.current?.show({
            severity: 'info',
            detail: 'No pages to extract',
            life: 5000,
          });
          return;
        }

        if (typeof valueLocal === 'object' && 'categoryId' in valueLocal) {
          setExtractedCategoryName(valueLocal.categoryName);
          triggerExtractTo({
            bundleId: bundleId,
            categoryId: valueLocal.categoryId,
            categoryName: null,
            pageIds: pageIdsToExtract,
            date: extractedDate,
          })
            .unwrap()
            .then(() =>
              sendMessageWithResponse(
                BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
                {
                  showPageIdInViewport: getPageToScrollTo(pageIdsToExtract),
                  bundleId: otherBundleId,
                },
                1000,
              ),
            )
            .then((response) => {
              if (response instanceof Window) response.focus();
            })
            .catch((error) => console.info(error.message));
        }

        if (typeof valueLocal === 'string') {
          const existingCategoryWithTheSameName = categoriesList.find(
            (category) => category.categoryName === valueLocal,
          );

          // If category with this name exist by don't selected manually.
          if (existingCategoryWithTheSameName) {
            setExtractedCategoryName(existingCategoryWithTheSameName.categoryName);
            triggerExtractTo({
              bundleId: bundleId,
              categoryId: existingCategoryWithTheSameName.categoryId,
              categoryName: null,
              pageIds: pageIdsToExtract,
              date: extractedDate,
            })
              .unwrap()
              .then(() =>
                sendMessageWithResponse(
                  BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
                  {
                    showPageIdInViewport: getPageToScrollTo(pageIdsToExtract),
                    bundleId: otherBundleId,
                  },
                  1000,
                ),
              )
              .then((response) => {
                if (response instanceof Window) response.focus();
              })
              .catch((error) => console.info(error.message));
          } else {
            setExtractedCategoryName(valueLocal);
            triggerExtractTo({
              bundleId: bundleId,
              categoryId: null,
              categoryName: valueLocal,
              pageIds: pageIdsToExtract,
              date: extractedDate,
            })
              .unwrap()
              .then(() =>
                sendMessageWithResponse(
                  BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
                  {
                    showPageIdInViewport: getPageToScrollTo(pageIdsToExtract),
                    bundleId: otherBundleId,
                  },
                  1000,
                ),
              )
              .then((response) => {
                if (response instanceof Window) response.focus();
              })
              .catch((error) => console.info(error.message));
          }
        }
      }

      setShowCategoriesPopup(false);
      setShowNewCategoryAutocomplete(false);
      setExtractedDate(null);
    },
    [
      activeBundleType,
      bundleId,
      globalToastRef,
      pageIdsNotInLightBundle,
      triggerExtractTo,
      pageId,
      getBundlePageDataByPageId,
      categoriesList,
      extractedDate,
      getPageToScrollTo,
      otherBundleId,
      pageInfoCache,
      sendMessageWithResponse,
    ],
  );

  return {
    isExtractToLoading,
    isExtractToSuccess,
    categoriesVirtualList,
    categoriesListRef,
    virtualizer,
    showCategoriesPopup,
    setExtractedDate,
    setShowCategoriesPopup,
    showNewCategoryAutocomplete,
    setShowNewCategoryAutocomplete,
    handleClickNewCategory,
    handleClickCancelNewCategory,
    handleClickSubmitNewCategory,
  };
};
